// payment validation mixin for stripe components
export default {
  methods: {
    // validate deal (quantity/article) selection
    validateDealSelection() {
      // for upsell payments, deal validation is not required since it's already selected
      if (this.upsells) {
        return true;
      }

      // check if deal is selected using validation object
      if (this.$v && this.$v.form && this.$v.form.deal) {
        this.$v.form.deal.$touch();
        
        if (this.$v.form.deal.$invalid) {
          console.warn('Deal selection is invalid');
          
          // scroll to deal selection area
          const element = document.querySelector(".main__deal");
          if (element && element.scrollIntoView) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
          
          // emit event to show promotional modal like PayPal does
          this.$emit('setPromotionalModal', true);
          
          return false;
        }
      }

      // fallback check using quantity prop
      if (!this.quantity || this.quantity <= 0) {
        console.warn('No deal/quantity selected');
        
        // scroll to deal selection area
        const element = document.querySelector(".main__deal");
        if (element && element.scrollIntoView) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
        
        // emit event to show promotional modal
        this.$emit('setPromotionalModal', true);
        
        return false;
      }

      return true;
    },

    // validate variant selection if required
    validateVariantSelection() {
      // for upsell payments, variant validation is not required
      if (this.upsells) {
        return true;
      }

      // check if variant selection is required and valid
      if (this.$v && this.$v.form && this.$v.form.variant) {
        this.$v.form.variant.$touch();
        
        if (this.$v.form.variant.$invalid) {
          console.warn('Variant selection is invalid');
          
          // scroll to variant selection area
          const element = document.querySelector(".variant-selection");
          if (element && element.scrollIntoView) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
          
          // emit event to show promotional modal like PayPal does
          this.$emit('setPromotionalModal', true);
          
          return false;
        }
      }

      // fallback check using sku prop
      if (!this.sku) {
        // check if variant selection is actually required
        const skusList = js_data?.product?.skus || [];
        const isChoiceRequired = js_data?.product?.is_choice_required;
        
        if (skusList.length > 1 && isChoiceRequired) {
          console.warn('No variant/sku selected when required');
          
          // scroll to variant selection area
          const element = document.querySelector(".variant-selection");
          if (element && element.scrollIntoView) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
          
          // emit event to show promotional modal
          this.$emit('setPromotionalModal', true);
          
          return false;
        }
      }

      return true;
    },

    // validate required personal data fields
    validatePersonalData() {
      // for upsell payments, personal data validation is not required
      // since the customer has already provided this information
      if (!this.paymentFormData) {
        console.log('Payment form data not provided - skipping validation for upsell payment');
        return true;
      }

      // trigger validation highlighting like PaymentForm does, but only for personal data fields
      if (this.$v && this.$v.form) {
        // only touch personal data fields, not card fields
        const personalDataFields = ['fname', 'lname', 'email', 'phone', 'street', 'city', 'country', 'zipcode'];
        personalDataFields.forEach(field => {
          if (this.$v.form[field]) {
            this.$v.form[field].$touch();
          }
        });
        
        // check if personal data fields have validation errors (ignore card fields)
        const hasPersonalDataErrors = personalDataFields.some(field => {
          return this.$v.form[field] && this.$v.form[field].$error;
        });
        
        if (hasPersonalDataErrors) {
          this.scrollToPersonalDataError();
          return false;
        }
      }

      const requiredFields = [
        'fname',
        'lname',
        'email',
        'phone',
        'street',
        'city',
        'country',
        'zipcode'
      ];

      const missingFields = requiredFields.filter(field => {
        const value = this.paymentFormData[field];
        return !value || (typeof value === 'string' && value.trim() === '');
      });

      if (missingFields.length > 0) {
        console.warn('Missing required personal data fields:', missingFields);
        // just scroll to the first empty field, don't show payment error
        this.scrollToPersonalDataError();
        return false;
      }

      // validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.paymentFormData.email)) {
        console.warn('Invalid email format');
        // scroll to email field
        this.scrollToPersonalDataError();
        return false;
      }

      return true;
    },

    // scroll to first personal data error field (ignore card fields for wallet payments)
    scrollToPersonalDataError() {
      if (!this.$v || !this.$v.form) return;
      
      // only check personal data fields, not card fields
      const personalDataFields = ['fname', 'lname', 'email', 'phone', 'street', 'city', 'country', 'zipcode'];
      const fieldNames = {
        'fname': 'first-name-field',
        'lname': 'last-name-field',
        'email': 'email-field',
        'phone': 'phone-field',
        'street': 'street-field',
        'city': 'city-field',
        'country': 'country-field',
        'zipcode': 'zipcode-field'
      };
      
      // find first personal data field with error
      for (let field of personalDataFields) {
        if (this.$v.form[field] && this.$v.form[field].$error) {
          const elementId = fieldNames[field];
          if (elementId) {
            const element = document.getElementById(elementId);
            if (element && element.scrollIntoView) {
              element.scrollIntoView({ behavior: 'smooth' });
              break;
            }
          }
        }
      }
    },

    // get dial code for phone number
    getDialCode() {
      if (!this.paymentFormData.countryCodePhoneField) {
        return '1'; // default to US
      }

      try {
        const allCountries = window.intlTelInputGlobals?.getCountryData();
        if (!allCountries) {
          return '1';
        }

        const country = allCountries.find(item =>
          item.iso2 === this.paymentFormData.countryCodePhoneField
        );
        
        return country ? country.dialCode : '1';
      } catch (error) {
        console.warn('Error getting dial code:', error);
        return '1';
      }
    }
  }
};